import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  memo,
  useCallback,
  useMemo,
} from 'react';
import { FaCaretDown } from 'react-icons/fa';
import { isNullOrUndefined } from 'utils/Global';
import RMDropDownList, { IDropDownList } from '../RMDropDownList';
import RMSvgIcon, { RMSvgIconProps } from '../RMSvgIcon';
import './RMCustomButton.scss';

export interface RMCustomButtonProps {
  color?: string;
  Icon?: any;
  title?: string | React.ReactNode;
  titleTip?: string;
  onClick?: any;
  showBorder?: boolean;
  showChevronIcon?: boolean;
  chevronIconInitialAngle?: number;
  chevronIconPosition?: 'start' | 'center' | 'end';
  dropDownList?: IDropDownList[];
  showCheckMarkOnSelected?: boolean;
  contentOrientation?: 'Vertical' | 'Horizontal';
  contentContainerClass?: string;
  contentContainerStyle?: any;
  buttonMainContainerStyle?: any;
  iconClass?: string;
  titleClass?: string;
  isDisabled?: boolean;
  active?: boolean;
  iconProps?: RMSvgIconProps;
  customStyle?: React.CSSProperties;
  mainContainerClass?: string;
  iconToDisplayOnHover?: any;
  id?: string;
}

const flexDirection = {
  Horizontal: 'flex-row',
  Vertical: 'flex-column',
};

const ChevronIconDivClass = {
  Horizontal: 'h-100',
  Vertical: 'w-100',
};

const ChevronIconAlignmentProperty = {
  Horizontal: 'align-items',
  Vertical: 'justify-content',
};
const RMCustomButton = forwardRef((props: RMCustomButtonProps) => {
  const {
    color = '#000000',
    Icon,
    title,
    titleTip,
    onClick,
    showBorder = false,
    showChevronIcon = false,
    chevronIconPosition = 'center',
    chevronIconInitialAngle = 0,
    contentOrientation = 'Horizontal',
    contentContainerClass,
    contentContainerStyle,
    buttonMainContainerStyle,
    iconClass,
    titleClass,
    dropDownList,
    showCheckMarkOnSelected = false,
    isDisabled,
    active,
    iconProps,
    customStyle,
    mainContainerClass,
    iconToDisplayOnHover,
    id,
  } = props;
  const ref = useRef<HTMLInputElement>();
  const [showDropDownList, setShowDropDownList] = useState<boolean>(false);
  const [iconSvg, setIconSvg] = useState(Icon);
  const controlContainerStyle = useMemo(
    () => ({
      color: color,
      border: showBorder ? 'solid' : 'None',
      borderWidth: showBorder ? '1px' : '0px',
      borderRadius: 5,
    }),
    [color, showBorder],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const current = ref.current;
      if (current) {
        if (!current?.contains(event.target)) {
          setShowDropDownList(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
  }, [ref]);

  const dropDownListHandler = useCallback(
    (e) => {
      if (onClick) {
        onClick(e);
      }
      setShowDropDownList((prev) => !prev);
    },
    [onClick],
  );

  const getOnClickHandler = useCallback(
    (e) => {
      if (isDisabled) {
        return null;
      } else {
        if (!dropDownList && !onClick) {
          return null;
        } else if (isNullOrUndefined(dropDownList)) {
          onClick(e);
        } else {
          dropDownListHandler(e);
        }
      }
    },
    [isDisabled, dropDownList, onClick],
  );

  const onMouseEnterEvent = () => {
    if (iconToDisplayOnHover) {
      setIconSvg(iconToDisplayOnHover);
    }
  };

  const onMouseLeaveEvent = () => {
    if (iconToDisplayOnHover) {
      setIconSvg(Icon);
    }
  };

  const identifier = titleTip ?? title ?? iconProps?.name.toLowerCase() ?? 'default';

  return (
    <div
      id={`rmcustombutton-main-container-${identifier}`}
      data-testid={`rmcustombutton-main-container-${identifier}`}
      onMouseEnter={onMouseEnterEvent}
      onMouseLeave={onMouseLeaveEvent}
      ref={ref}
      className={`${mainContainerClass} rm-custom-button-main-container`}
      {...(titleTip && { title: titleTip })}
      style={{ ...buttonMainContainerStyle }}
    >
      <div
        id={`rmcustombutton-control-container-${identifier}`}
        data-testid={`rmcustombutton-control-container-${identifier}`}
        onClick={getOnClickHandler}
        className={`${contentContainerClass} rm-custom-button-control-container  ${
          isDisabled ? 'rm-custom-button-control-container-disabled' : 'cursor-pointer'
        } ${active ? 'rm-custom-button-control-container-active' : ''} ${
          flexDirection[contentOrientation]
        }`}
        style={{ ...controlContainerStyle, ...customStyle, ...contentContainerStyle }}
      >
        {(iconSvg || iconProps) && (
          <div
            id={`rmcustombutton-${identifier}-icon`}
            data-testid={`rmcustombutton-${identifier}-icon`}
            className={`${iconClass} d-flex justify-content-center`}
          >
            {iconSvg}
            {iconProps && (
              <RMSvgIcon
                id={`rmcustombutton-svg-${identifier}-icon`}
                data-testid={`rmcustombutton-svg-${identifier}-icon`}
                {...iconProps}
              />
            )}
          </div>
        )}
        {!isNullOrUndefined(title) && (
          <div
            id={`rmcustombutton-${identifier}-title`}
            data-testid={id ?? `rmcustombutton-${identifier}-title`}
            className={`${titleClass} rm-custom-button-control-title-text`}
          >
            {title}
          </div>
        )}
        {showChevronIcon && (
          <div
            id={`rmcustombutton-chevronicon-for-${identifier}`}
            data-testid={`rmcustombutton-chevronicon-for-${identifier}`}
            className={`d-flex ${ChevronIconDivClass[contentOrientation]}
             ${ChevronIconAlignmentProperty[contentOrientation]}-${chevronIconPosition}
            `}
          >
            <FaCaretDown
              id={`${
                showDropDownList
                  ? ' rmcustombutton-control-chevron-icon-transform-up'
                  : ' rmcustombutton-control-chevron-icon-transform-down'
              }-for-${identifier}`}
              data-testid={`${
                showDropDownList
                  ? 'rmcustombutton-control-chevron-icon-transform-up'
                  : 'rmcustombutton-control-chevron-icon-transform-down'
              }-for-${identifier}`}
              className={`${
                showDropDownList
                  ? ' rm-custom-button-control-chevron-icon-transform-up'
                  : ' rm-custom-button-control-chevron-icon-transform-down'
              }`}
              style={{ rotate: `${chevronIconInitialAngle}deg` }}
              color={color}
            />
          </div>
        )}
      </div>
      {!isNullOrUndefined(dropDownList) && (
        <RMDropDownList
          parentContainerRef={ref}
          setShowDropDownList={setShowDropDownList}
          showDropDownList={showDropDownList}
          dropDownList={dropDownList}
          showCheckMarkOnSelected={showCheckMarkOnSelected}
        />
      )}
    </div>
  );
});

export default memo(RMCustomButton);
